import { dbToGain } from './levels';

/** spin-tick, plus the a–d variants. One is chosen at random each wedge. */
export const SPIN_TICK_FILES = ['spin-tick', 'spin-tick-a', 'spin-tick-b', 'spin-tick-c', 'spin-tick-d'] as const;

export const SPIN_TICK_DB = 1;

/** Peak of a 13-turn ease. The bed is already quiet, so this speed plays it at unity. */
export const SPIN_BED_REF_DEG_PER_SEC = 1100;

/** Wind-back ratchet, a short beat, then the zing. The hold completes as the zing starts. */
export const SPIN_WINDBACK_FILE = 'spin-windback-zing-b';

/** Ratchet ticks inside the clip, in milliseconds. */
export const SPIN_WINDBACK_TICK_MS = [20, 330, 610] as const;

/**
 * Zing onset in `spin-windback-zing-b`, measured on the ogg the game plays.
 * The clicks attack at 21, 331, and 611 ms. The third click is quiet by 784 ms.
 * Silence holds until 911 ms, where the zing's first samples rise
 * (ffmpeg silencedetect -40 dB ends at 910.7 ms; the m4a matches).
 * The wheel page opens on this beat. A muted hold waits the same delay.
 */
export const SPIN_WINDBACK_ZING_MS = 911;

export const SPIN_HOLD_MS = SPIN_WINDBACK_ZING_MS;

/** Same length as the chest hold fade: short enough to feel immediate, long enough to avoid a click. */
export const SPIN_HOLD_FADE_SEC = 0.03;

/** Pulses on the three ticks. Each one is longer than the last. */
export const SPIN_TICK_HAPTIC_MS = [16, 28, 40] as const;

/** Stronger than the last tick. Fires when the hold completes on the zing. */
export const SPIN_ZING_HAPTIC_MS = 64;

export const SPIN_WINDBACK_PULSES = [
  { at: SPIN_WINDBACK_TICK_MS[0], ms: SPIN_TICK_HAPTIC_MS[0] },
  { at: SPIN_WINDBACK_TICK_MS[1], ms: SPIN_TICK_HAPTIC_MS[1] },
  { at: SPIN_WINDBACK_TICK_MS[2], ms: SPIN_TICK_HAPTIC_MS[2] },
] as const;

/**
 * Shake charge locked to the ratchet. Quiet before the first tick, then a
 * third, two thirds, and full after the third tick. Reduced motion still
 * zeroes the pixels through chestShakePx.
 */
export function spinWindbackCharge(elapsedMs: number): number {
  if (elapsedMs >= SPIN_WINDBACK_TICK_MS[2]) return 1;
  if (elapsedMs >= SPIN_WINDBACK_TICK_MS[1]) return 2 / 3;
  if (elapsedMs >= SPIN_WINDBACK_TICK_MS[0]) return 1 / 3;
  return 0;
}

export const SPIN_AUDIO_FILES = [
  'spin-button',
  'spin-windup',
  'spin-bed-loop',
  'spin-settle',
  'win-common',
  'win-mid',
  'jackpot-big-1',
  SPIN_WINDBACK_FILE,
  ...SPIN_TICK_FILES,
] as const;

export function spinTickFile(random: () => number = Math.random): (typeof SPIN_TICK_FILES)[number] {
  const index = Math.min(SPIN_TICK_FILES.length - 1, Math.max(0, Math.floor(random() * SPIN_TICK_FILES.length)));
  return SPIN_TICK_FILES[index] ?? 'spin-tick';
}

export function spinTickGain(): number {
  return dbToGain(SPIN_TICK_DB);
}

export function spinBedGain(degPerSec: number): number {
  if (!Number.isFinite(degPerSec)) return 0;
  return Math.max(0, Math.min(1, Math.abs(degPerSec) / SPIN_BED_REF_DEG_PER_SEC));
}

export function spinWinFile(kind: 'common' | 'mid' | 'jackpot'): string {
  if (kind === 'common') return 'win-common';
  if (kind === 'jackpot') return 'jackpot-big-1';
  return 'win-mid';
}

interface SpinBuffer {
  duration: number;
}

interface SpinGain {
  value: number;
  cancelScheduledValues?(when: number): void;
  setValueAtTime?(value: number, when: number): void;
  linearRampToValueAtTime?(value: number, when: number): void;
}

interface SpinSource {
  buffer: SpinBuffer | null;
  loop: boolean;
  connect(node: unknown): void;
  start(when?: number): void;
  stop(when?: number): void;
}

interface SpinContext {
  currentTime: number;
  createBufferSource(): SpinSource;
  createGain(): { gain: SpinGain; connect(node: unknown): void };
}

/**
 * Wheel cues on the effects bus. The bed follows angular speed.
 * A jackpot stops the bed. Mute and the effects switch stop everything.
 */
export class SpinCue {
  private bedToken = 0;
  private bedSrc: SpinSource | null = null;
  private bedGain: { gain: SpinGain } | null = null;
  private wantBed = false;
  private windToken = 0;
  private winding = false;
  private windSrc: SpinSource | null = null;
  private windGain: SpinGain | null = null;
  private readonly live = new Set<SpinSource>();

  constructor(
    private readonly context: () => SpinContext | null,
    private readonly bus: () => unknown,
    private readonly silenced: () => boolean,
    private readonly load: (file: string) => Promise<SpinBuffer | null>,
  ) {}

  preload(): void {
    for (const file of SPIN_AUDIO_FILES) void this.load(file);
  }

  button(): void {
    this.play('spin-button', 1);
  }

  windup(): void {
    this.play('spin-windup', 1);
  }

  /** Hub hold. A late decode is dropped if the player already let go. */
  windback(): void {
    this.windToken += 1;
    const token = this.windToken;
    this.winding = true;
    this.stopWind(0);
    if (this.silenced()) return;
    void this.load(SPIN_WINDBACK_FILE).then((buffer) => {
      if (!buffer || token !== this.windToken || !this.winding || this.silenced()) return;
      const started = this.startOne(buffer, 1, false);
      if (!started) return;
      this.windSrc = started.src;
      this.windGain = started.amp.gain;
    });
  }

  /** Early release. Linear fade, about 30ms, then the source stops. */
  fadeWind(fadeSec = SPIN_HOLD_FADE_SEC): void {
    this.winding = false;
    this.windToken += 1;
    this.stopWind(fadeSec);
  }

  tick(random: () => number = Math.random): void {
    this.play(spinTickFile(random), spinTickGain());
  }

  settle(): void {
    this.play('spin-settle', 1);
  }

  /** win-mid is left to finish. The crown stops the bed first so the fanfare is clear. */
  win(kind: 'common' | 'mid' | 'jackpot'): void {
    if (kind === 'jackpot') this.stopBed();
    this.play(spinWinFile(kind), 1);
  }

  /** Starts the loop if needed and sets its gain from the wheel's speed. */
  bed(degPerSec: number): void {
    if (this.silenced()) {
      this.stopBed();
      return;
    }
    this.wantBed = true;
    const amount = spinBedGain(degPerSec);
    if (this.bedGain) {
      this.bedGain.gain.value = amount;
      return;
    }
    this.startBed(amount);
  }

  stopBed(): void {
    this.wantBed = false;
    this.bedToken += 1;
    const src = this.bedSrc;
    this.bedSrc = null;
    this.bedGain = null;
    this.stop(src);
  }

  halt(): void {
    this.winding = false;
    this.windToken += 1;
    this.stopWind(0);
    this.stopBed();
    for (const src of [...this.live]) this.stop(src);
    this.live.clear();
  }

  private play(file: string, gain: number): void {
    if (this.silenced()) return;
    void this.load(file).then((buffer) => {
      if (!buffer || this.silenced()) return;
      this.startOne(buffer, gain, false);
    });
  }

  private startBed(gain: number): void {
    const token = this.bedToken;
    void this.load('spin-bed-loop').then((buffer) => {
      if (!buffer || token !== this.bedToken || !this.wantBed || this.silenced()) return;
      const started = this.startOne(buffer, gain, true);
      if (!started) return;
      this.bedSrc = started.src;
      this.bedGain = started.amp;
    });
  }

  private startOne(
    buffer: SpinBuffer,
    gain: number,
    loop: boolean,
  ): { src: SpinSource; amp: { gain: SpinGain } } | null {
    const ctx = this.context();
    const dest = this.bus();
    if (!ctx || !dest) return null;
    const src = ctx.createBufferSource();
    const amp = ctx.createGain();
    amp.gain.value = gain;
    src.buffer = buffer;
    src.loop = loop;
    src.connect(amp);
    amp.connect(dest);
    this.live.add(src);
    try {
      src.start();
    } catch {
      this.live.delete(src);
      return null;
    }
    return { src, amp };
  }

  private stopWind(fadeSec: number): void {
    const src = this.windSrc;
    const amp = this.windGain;
    this.windSrc = null;
    this.windGain = null;
    if (!src) return;
    this.live.delete(src);
    const ctx = this.context();
    const cancel = amp?.cancelScheduledValues;
    const setAt = amp?.setValueAtTime;
    const ramp = amp?.linearRampToValueAtTime;
    if (!ctx || !amp || fadeSec <= 0 || !cancel || !setAt || !ramp) {
      this.stop(src);
      return;
    }
    const now = ctx.currentTime;
    try {
      cancel.call(amp, now);
      setAt.call(amp, amp.value, now);
      ramp.call(amp, 0.0001, now + fadeSec);
      src.stop(now + fadeSec);
    } catch {
      this.stop(src);
    }
  }

  private stop(src: SpinSource | null): void {
    if (!src) return;
    this.live.delete(src);
    try {
      src.stop();
    } catch {
      // Already finished.
    }
  }
}
